import type { IChannel } from "containers/Channels/types";
import { useMemo } from "react";

import type { ChannelParams } from "../..";
import type { GroupWithChannels } from "../types";

type ContractFormMode = "new" | "edit";

interface DisplayPartition {
    channelIds: number[] | null;
    groupIds: number[] | null;
}

export function useContractChannelLists(
    mode: ContractFormMode,
    orderedChannels: IChannel[],
    chosenChannels: ChannelParams[],
    orderedGroups: GroupWithChannels[],
    activeGroups: number[],
    displayPartition?: DisplayPartition,
) {
    const partitionReady =
        mode !== "edit" || (displayPartition?.channelIds != null && displayPartition?.groupIds != null);

    const activeChannelIds = useMemo(() => {
        if (mode !== "edit") {
            return new Set(chosenChannels?.map((item) => item.channelId) ?? []);
        }
        if (displayPartition?.channelIds != null) {
            return new Set(displayPartition.channelIds);
        }
        return new Set(chosenChannels?.map((item) => item.channelId) ?? []);
    }, [mode, chosenChannels, displayPartition?.channelIds]);

    const activeGroupIds = useMemo(() => {
        if (mode !== "edit") {
            return activeGroups;
        }
        if (displayPartition?.groupIds != null) {
            return displayPartition.groupIds;
        }
        return activeGroups;
    }, [mode, activeGroups, displayPartition?.groupIds]);

    const { activeChannelsList, inactiveChannelsList } = useMemo(() => {
        if (mode !== "edit") {
            return { activeChannelsList: orderedChannels, inactiveChannelsList: [] as IChannel[] };
        }
        if (!partitionReady) {
            return { activeChannelsList: orderedChannels, inactiveChannelsList: [] as IChannel[] };
        }
        return {
            activeChannelsList: orderedChannels.filter((channel) => activeChannelIds.has(channel.id)),
            inactiveChannelsList: orderedChannels.filter((channel) => !activeChannelIds.has(channel.id)),
        };
    }, [mode, orderedChannels, activeChannelIds, partitionReady]);

    const { activeGroupsList, inactiveGroupsList } = useMemo(() => {
        if (mode !== "edit") {
            return { activeGroupsList: orderedGroups, inactiveGroupsList: [] as GroupWithChannels[] };
        }
        if (!partitionReady) {
            return { activeGroupsList: orderedGroups, inactiveGroupsList: [] as GroupWithChannels[] };
        }
        return {
            activeGroupsList: orderedGroups.filter((group) => activeGroupIds.includes(group.id as number)),
            inactiveGroupsList: orderedGroups.filter((group) => !activeGroupIds.includes(group.id as number)),
        };
    }, [mode, orderedGroups, activeGroupIds, partitionReady]);

    return { activeChannelsList, inactiveChannelsList, activeGroupsList, inactiveGroupsList };
}
